const MAX_TAP_LOTS = 24

// Keep token quantities exact, including TAP balances above Number.MAX_SAFE_INTEGER.
export function decimalUnits(value: string, decimals: number): bigint {
	if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18 || !/^\d+(\.\d+)?$/.test(value))
		throw new Error('Enter a valid token amount.')
	const [whole, fraction = ''] = value.split('.')
	if (fraction.length > decimals) throw new Error(`Use at most ${decimals} decimal places.`)
	return BigInt(whole) * 10n ** BigInt(decimals) + BigInt(fraction.padEnd(decimals, '0') || '0')
}
export function formatUnits(value: bigint, decimals: number): string {
	const scale = 10n ** BigInt(decimals)
	const fraction = (value % scale).toString().padStart(decimals, '0').replace(/0+$/, '')
	return `${value / scale}${decimals && fraction ? `.${fraction}` : ''}`
}
export function planTapListings(
	amount: string,
	available: string,
	decimals: number,
	price: string,
	target: string | number,
	exchangeRate: number
) {
	const units = decimalUnits(amount, decimals)
	if (units <= 0n || units > decimalUnits(available, decimals))
		throw new Error('Choose an amount within your available balance.')
	if (!price || !Number.isFinite(exchangeRate) || exchangeRate <= 0)
		throw new Error('Prices are refreshing. Try again in a moment.')
	const sats = decimalUnits(price, 8)
	if (sats <= 0n) throw new Error('Prices are refreshing. Try again in a moment.')
	const scale = 10n ** BigInt(decimals)
	let quantities: bigint[]
	if (typeof target === 'number') {
		if (!Number.isInteger(target) || target < 1 || target > MAX_TAP_LOTS)
			throw new Error(`Choose between 1 and ${MAX_TAP_LOTS} outputs.`)
		const count = BigInt(target)
		if (units < count) throw new Error('Too many outputs for this amount. Choose fewer.')
		const each = units / count
		const remainder = units % count
		// Match the other split forms: equal lots, distributing remaining atoms exactly.
		quantities = Array.from({ length: target }, (_, i) => each + (BigInt(i) < remainder ? 1n : 0n))
	} else {
		const dollars = target === 'single' ? 1n : decimalUnits(target, 2)
		if (dollars <= 0n) throw new Error('Enter a positive listing size.')
		const rate = BigInt(Math.round(exchangeRate * 100))
		const targetUnits =
			target === 'single' ? units : (scale * 100000000n * 100000000n * dollars) / (sats * rate)
		const lotUnits = targetUnits > 0n ? targetUnits : 1n
		const fullCount = units / lotUnits
		const remainder = units % lotUnits
		const remainderSats = (remainder * sats + scale * 50000000n) / (scale * 100000000n)
		const mergeRemainder = fullCount > 0n && remainder > 0n && remainderSats < 1000n
		const count = fullCount + (remainder > 0n && !mergeRemainder ? 1n : 0n)
		if (count > BigInt(MAX_TAP_LOTS))
			throw new Error(
				`This needs ${count} listings. Prepare a smaller amount or increase the target size (up to ${MAX_TAP_LOTS} listings per batch).`
			)
		quantities = Array.from({ length: Number(fullCount) }, () => lotUnits)
		if (mergeRemainder) quantities[quantities.length - 1] += remainder
		else if (remainder > 0n) quantities.push(remainder)
	}
	const amounts = quantities.map((quantity) => formatUnits(quantity, decimals))
	const prices = amounts.map((a) =>
		Number((decimalUnits(a, decimals) * sats + scale * 50000000n) / (scale * 100000000n))
	)
	if (prices.some((p) => !Number.isSafeInteger(p) || p < 1000))
		throw new Error(
			typeof target === 'number'
				? 'Each lot must be worth at least 1,000 sats. Reduce the number of outputs or increase the amount.'
				: 'Each listing must be worth at least 1,000 sats. Increase the target size or amount.'
		)
	const gross = prices.reduce((a, b) => a + b, 0)
	if (!Number.isSafeInteger(gross)) throw new Error('Prepare a smaller amount.')
	const fees = prices.reduce((sum, p) => sum + Math.max(Math.round(p * 0.027), 1000), 0)
	return { amounts, gross, fees, proceeds: gross - fees }
}
export interface PreparedTapListings {
	ids: string[]
	price: string
}

// Aim for at least two affordable lots, capped at $200, without defaulting
// to tiny lots whose preparation and minimum marketplace fees dominate.
export function recommendedTapLotSize(grossSats: number, exchangeRate: number): string {
	const dollars = (grossSats / 1e8) * exchangeRate
	return [200, 100, 50, 25].find((size) => dollars >= size * 2)?.toString() ?? 'single'
}

// Use the existing dollar-size recommendation as the initial output count.
export function recommendedTapOutputCount(grossSats: number, exchangeRate: number): number {
	const size = recommendedTapLotSize(grossSats, exchangeRate)
	return size === 'single'
		? 1
		: Math.min(MAX_TAP_LOTS, Math.ceil(((grossSats / 1e8) * exchangeRate) / Number(size)))
}
